import { FunctionComponent, useState, useEffect } from 'react';
import InquiryActionPopupWrapper from './InquiryActionPopup.style';
import Checkbox from '@element/Checkbox/Checkbox';
import FursysService from '@service/fursysService';
import { Toast } from '@component/Toast';
import Alert from '@component/Alert';
import CloseIcon from '../../public/icon/CloseMedium';
import { validPhoneNumber } from '../../utils/validation';
import { sendAmplitudeEvent } from '../../utils/amplitude';

type QuestionActionPopupType = {
  clickCancel: () => void;
};

const InquiryActionPopup: FunctionComponent<QuestionActionPopupType> = ({ clickCancel }) => {
  const [checked, setChecked] = useState(false);
  const [showIndividual, setShowIndividual] = useState(false);
  const [sendData, setSendData] = useState({ name: '', office: '', phone: '', msg: '' });
  const [toastMessage, setToastMessage] = useState('');
  const alertResetdata = { open: false, description: '' };
  const [visibleAlert, setVisibleAlert] = useState(alertResetdata);
  const [isComplete, setIsComplete] = useState(false);

  const agreeIndividual = () => {
    setChecked(!checked);
  };

  const openIndividual = () => {
    setShowIndividual(!showIndividual);
  };

  const clickConfirm = () => {
    setVisibleAlert(alertResetdata);
    clickCancel();
  };
  const completeQuestion = async () => {
    if (sendData.name.length === 0) {
      setToastMessage('이름은 반드시 입력하셔야 합니다.');
      return;
    }
    if (sendData.office.length === 0) {
      setToastMessage('사무소명을 입력해 주세요.');
      return;
    }

    if (sendData.phone.length === 0) {
      setToastMessage('연락처를 입력해주세요');
      return;
    }

    const isPhone = validPhoneNumber(sendData.phone);
    if (!isPhone) {
      setToastMessage('올바른 연락처를 입력해주세요');
      return;
    }

    if (!checked) {
      setToastMessage('개인정보 수집/이용동의가 필요합니다');
      return;
    }

    try {
      // 유효성 검사만 마치면, 데이터 통신 성공여부와 관계없이 앰플리튜드 이벤트 송출
      sendAmplitudeEvent('sqr_click_fursys', { label: 'submit' });
      const result = await FursysService.sendQuestion(sendData);
      if (result.status === 200) {
        setIsComplete(true);
        setVisibleAlert({ open: true, description: '문의 신청이 완료되었습니다.' });
        return;
      } else {
        throw Error();
      }
    } catch (error) {
      console.error(error);
      setVisibleAlert({ open: true, description: '전송해 실패하였습니다 잠시 후 다시 시도해 주세요.' });
      return;
    }
  };

  const changeContents = (e: any) => {
    setSendData({ ...sendData, msg: e.currentTarget.value });
  };

  const changePhone = (e: any) => {
    const phoneValue = e.currentTarget.value;

    setSendData({ ...sendData, phone: phoneValue.toString().replace(/[^0-9]/g, '') });
  };

  const changeName = (e: any) => {
    setSendData({ ...sendData, name: e.currentTarget.value });
  };

  const changeOffice = (e: any) => {
    setSendData({ ...sendData, office: e.currentTarget.value });
  };

  useEffect(() => {
    if (toastMessage.length > 0) {
      setTimeout(() => {
        setToastMessage('');
      }, 3000);
    }
  }, [toastMessage]);

  return (
    <InquiryActionPopupWrapper>
      <Toast message={toastMessage} visible={toastMessage.length > 0} />
      <button onClick={clickCancel} className="closeButton desktop">
        <CloseIcon />
      </button>
      <p className="questionpopup--title">문의하기</p>
      <div className="questionpopup--body">
        <div className="questionpopup--body-editor name">
          <input
            className="questionpopup--body-text name"
            placeholder="이름을 입력해 주세요."
            onChange={changeName}
            value={sendData.name}
            type="text"
          />
        </div>
        <div className="questionpopup--body-editor office">
          <input
            className="questionpopup--body-text office"
            placeholder="소속을 입력해 주세요."
            onChange={changeOffice}
            value={sendData.office}
            type="text"
          />
        </div>
        <div className="questionpopup--body-editor phone">
          <input
            className="questionpopup--body-text phone"
            placeholder="휴대폰 번호를 입력해주세요."
            onChange={changePhone}
            value={sendData.phone}
            type="text"
          />
        </div>
        <div className="questionpopup--body-editor">
          <textarea
            className="questionpopup--body-text"
            placeholder="문의 내용을 입력해 주세요.(선택사항)"
            maxLength={100}
            onChange={changeContents}
          />
        </div>
      </div>
      <div className="questionpopup--individual-wrap">
        <div className="questionpopup--individual">
          <Checkbox checked={checked} label="개인정보수집동의" clickHandler={agreeIndividual} />
          <button className="questionpopup--individual-button" onClick={openIndividual}>
            {showIndividual ? '내용접기' : '내용보기'}
          </button>
        </div>
        <div className={`questionpopup--individual-table ${showIndividual && 'show'}`}>
          <div className="questionpopup--individual-table-column">
            <p className="questionpopup--individual-table-header">수집이용 목적</p>
            <p className="questionpopup--individual-table-body">
              문의 내용 답변,
              <br />
              상품안내
            </p>
          </div>
          <div className="questionpopup--individual-table-column">
            <p className="questionpopup--individual-table-header">수집항목</p>
            <p className="questionpopup--individual-table-body">
              이름, 연락처,
              <br />
              법률사무소명
            </p>
          </div>
          <div className="questionpopup--individual-table-column">
            <p className="questionpopup--individual-table-header">보유기간</p>
            <p className="questionpopup--individual-table-body"> 2주 </p>
          </div>
        </div>
      </div>
      <div className="questionpopup--buttons">
        <button className="questionpopup--button delete mobile" onClick={clickCancel}>
          취소
        </button>
        <button className="questionpopup--button complete" onClick={completeQuestion}>
          제출하기
        </button>
      </div>
      {visibleAlert.open && (
        <div id={`questionpopup--${isComplete ? 'complete' : 'notyet'}-alert`}>
          <Alert title="문의하기" description={visibleAlert.description} clickConfirm={clickConfirm} />
        </div>
      )}
    </InquiryActionPopupWrapper>
  );
};

export default InquiryActionPopup;
